<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from "vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { t } from "@/plugins/i18n";
import useDialog from "@/ui/composables/useDialog";

// Components
import ModuleLoading from "@/ui/components/loading/moduleLoading.vue";

// Types
import PriceModelCurveDO = Modules.BookingExperts.Data.PriceModelCurve.PriceModelCurveDO;
import CurveCreationModal from "@/modules/booking_experts/pages/Components/Modals/CurveCreationModal.vue";
import { debounce } from "vue-debounce";
import CurveCloneModal from "@/modules/booking_experts/pages/Components/Modals/CurveCloneModal.vue";
import { useBaseStore } from "@/stores/base";
import MasterPriceDO = Modules.BookingExperts.Data.MasterPriceList.MasterPriceDO;
import PricelistsForCurveModal from "@/modules/booking_experts/pages/Components/Modals/PricelistsForCurveModal.vue";

const dialog = useDialog();
const api = useApiClient("bookingExperts");
const fileInput = ref<HTMLInputElement | null>(null);

// State
const query = ref("");
const loading = shallowRef(false);
const showModal = ref(false);
const curves = ref<PriceModelCurveDO[]>([]);
const showCloneCurve = ref(false);
const showPricelistsForCurve = ref(false);

// Table configuration
const tableHeaders = [
	{ title: t("pricelist.curves.table.headers.name"), key: "name" },
	{
		title: t("pricelist.curves.table.headers.type"),
		key: "type",
		align: "start",
		sortable: true,
	},
	{
		title: t("pricelist.curves.table.headers.usageCount"),
		key: "count",
		align: "start",
		sortable: false,
	},
	{
		title: t("pricelist.curves.table.headers.description"),
		key: "description",
		align: "start",
		sortable: false,
	},
	{
		title: t("pricelist.curves.table.headers.actions"),
		key: "action",
		align: "end",
		sortable: false,
	},
];

const baseStore = useBaseStore();
const pricelists = ref<MasterPriceDO[]>([]);
const selectedPricelistId = ref<number>(-1);

const pricelistItems = computed(() => [
	{ id: -1, name: "All" },
	...pricelists.value
		.filter((pl) => pl.is_source)
		.map(({ id, name }) => ({ id, name })),
]);

// API calls
async function fetchPriceModelCurves() {
	loading.value = true;

	const pricelistIds = pricelists.value
		? pricelists.value.map((pl) => pl.id)
		: [];

	let result;

	if (
		baseStore.selectedBexAdministration.id === -1 &&
		selectedPricelistId.value === -1
	) {
		result = await api.getPriceModelCurves();
	} else if (selectedPricelistId.value === -1) {
		result = await api.getCurvesForAllPriceLists(pricelistIds);
	} else {
		result = await api.getCurvesForPriceList(selectedPricelistId.value);
	}

	if (result.isOk()) {
		curves.value = result.value;
	}
	loading.value = false;
}

async function fetchMasterPrices(): Promise<void> {
	loading.value = true;
	const params =
		baseStore.selectedBexAdministration.id !== -1
			? { "filter[administration_id]": baseStore.selectedBexAdministration.id }
			: {};
	const result = await api.getMasterPrices(params);

	if (result.isOk()) {
		pricelists.value = result.value;
	}
	loading.value = false;
}

async function deleteCurve(id: number) {
	const choice = await dialog.confirm({
		title: t("pricelist.curves.dialogs.delete.title"),
		message: t("pricelist.curves.dialogs.delete.message"),
		isDelete: true,
	});

	if (!choice) return;

	loading.value = true;
	const result = await toastApiResult(api.deletePriceModelCurve(id));

	if (result.isOk()) {
		curves.value = curves.value.filter((item) => item.id !== id);
	}
	loading.value = false;
}

function triggerFileInput() {
	if (fileInput.value) {
		fileInput.value.click();
	}
}

async function handleFileImport(event: Event) {
	const target = event.target as HTMLInputElement;
	if (!target.files?.length) return;

	const file = target.files[0];
	loading.value = true;

	try {
		const importedData = JSON.parse(await file.text());
		await importCurve(importedData);
		await fetchPriceModelCurves();

		await dialog.alert({
			title: "Import Successful",
			message: "Your curve model has been imported successfully.",
		});
	} catch (error) {
		console.error("Import error:", error);
		await dialog.alert({
			title: "Import Failed",
			message:
				"There was an error importing your curve model. Please check the file format and try again.",
		});
	} finally {
		loading.value = false;
		if (fileInput.value) fileInput.value.value = "";
	}
}

async function importCurve(data: any) {
	const result = await api.createPriceModelCurve(data);
	if (result.isOk()) {
		await toastApiResult(api.updatePriceModelCurve(result.value.id, data));
	}
}

async function exportCurve(item: PriceModelCurveDO) {
	try {
		const exportData = { ...item };
		delete exportData.id;

		const jsonString = JSON.stringify(exportData, null, 2);
		const url = URL.createObjectURL(
			new Blob([jsonString], { type: "application/json" })
		);

		const a = document.createElement("a");
		a.href = url;
		a.download = `${item.name.toLowerCase().replace(" ", "_")}_curve.json`;

		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);

		URL.revokeObjectURL(url);
	} catch (error) {
		await dialog.alert({
			title: "Export Failed",
			message: "There was an error exporting the curve model.",
		});
	}
}

const dFn = debounce(async (id: number, color: string) => {
	loading.value = true;
	await toastApiResult(
		api.updatePriceModelCurve(id, {
			color,
		})
	);
	loading.value = false;
}, "400ms");

const curve = ref<PriceModelCurveDO>();

function cloneCurve(item: PriceModelCurveDO) {
	showCloneCurve.value = true;
	curve.value = item;
}

function openPricelistsForCurve(item: PriceModelCurveDO) {
	showPricelistsForCurve.value = true;
	curve.value = item;
}

watch(
	() => baseStore.selectedBexAdministration?.id,
	async () => {
		await fetchMasterPrices();
		await fetchPriceModelCurves();
	}
);

watch(selectedPricelistId, () => fetchPriceModelCurves());

onMounted(async () => {
	baseStore.selectedBexAdministration = { id: -1, name: "All" };
	await fetchMasterPrices();
	await fetchPriceModelCurves();
});

onUnmounted(() => {
	baseStore.selectedBexAdministration = baseStore.bexAdministrations[0];
});
</script>

<template>
	<!-- Create New Model Modal -->
	<CurveCreationModal
		v-model="showModal"
		@curve-created="fetchPriceModelCurves"
	/>

	<CurveCloneModal
		v-if="curve"
		:key="curve.id"
		v-model:visible="showCloneCurve"
		:curve="curve"
		@duplicated="(curve) => curves.unshift(curve)"
	/>

	<PricelistsForCurveModal
		v-if="curve"
		:key="curve.id"
		v-model:visible="showPricelistsForCurve"
		:curve="curve"
	/>

	<!--	 Curves Table -->
	<module-loading>
		<v-card class="mt-5 pt-5 px-5 pb-3">
			<div class="top-row">
				<v-text-field
					v-model="query"
					label="Search"
					prepend-inner-icon="mdi-magnify"
					hide-details
					single-line
				/>
				<v-autocomplete
					v-model="selectedPricelistId"
					:items="pricelistItems"
					hide-details
					prepend-inner-icon="fa fa-calendars"
					item-value="id"
					item-title="name"
				/>
				<div class="position-relative">
					<v-btn
						height="48"
						variant="flat"
						color="secondary"
						prepend-icon="mdi-upload"
						@click="triggerFileInput"
					>
						{{ t("pricelist.curves.table.actions.importModel") }}
					</v-btn>
					<input
						ref="fileInput"
						type="file"
						accept=".json"
						@change="handleFileImport"
						class="file-input-overlay"
					/>
				</div>
				<v-btn
					height="48"
					variant="flat"
					color="primary"
					prepend-icon="mdi-plus"
					@click="showModal = true"
				>
					{{ t("pricelist.curves.table.actions.addModel") }}
				</v-btn>
			</div>
			<v-data-table
				:headers="tableHeaders"
				:items="curves"
				:loading="loading"
				:search="query"
			>
				<template #item="{ item }">
					<tr class="curve-item">
						<td class="name">
							<div class="cell-inner">
								<v-menu :close-on-content-click="false" location="bottom">
									<template v-slot:activator="{ props }">
										<v-btn
											elevation="0"
											v-bind="props"
											variant="text"
											size="30"
											max-width="30"
											class="flex-grow-1 justify-center"
										>
											<span
												class="chit"
												:style="`background: ${item.color}; margin: 0;`"
											/>
										</v-btn>
									</template>

									<v-card max-width="300" height="230">
										<v-color-picker
											@update:model-value="(color) => dFn(item.id, color)"
											v-model="item.color"
											elevation="0"
											hide-canvas
											hide-sliders
											hide-inputs
											show-swatches
											swatches-max-height="230"
											:modes="['hex']"
											width="100%"
										/>
									</v-card>
								</v-menu>
								<span class="text-name">{{ item.name }}</span>
							</div>
						</td>
						<td>
							{{ t(`pricelist.curves.${item.type}`) }}
						</td>
						<td class="count">
							<div class="cell-inner">
								<span class="count-number">{{ item.usage_count }}</span>
								<v-btn
									v-if="item.usage_count > 0"
									size="30"
									variant="text"
									@click="openPricelistsForCurve(item)"
									v-tooltip="
										t('pricelist.curves.table.actions.showCurveUsages')
									"
								>
									<i class="fa-solid fa-circle-info"></i>
								</v-btn>
							</div>
						</td>
						<td class="description">{{ item.description }}</td>
						<td class="actions">
							<v-btn
								size="30"
								variant="text"
								color="primary"
								:to="`curves/${item.id}`"
								v-tooltip="t('pricelist.curves.table.actions.defineCurve')"
								data-testid="view-curve"
							>
								<i class="fa-light fa-chart-line text-primary" />
							</v-btn>
							<v-btn
								size="30"
								variant="text"
								@click="cloneCurve(item)"
								v-tooltip="t('pricelist.curves.table.actions.cloneCurve')"
							>
								<i class="fa-light fa-clone" />
							</v-btn>
							<v-btn
								size="30"
								variant="text"
								color="info"
								@click="exportCurve(item)"
								v-tooltip="'Export Curve Model'"
							>
								<i class="fa-solid fa-down-to-line text-info" />
							</v-btn>
							<v-btn
								size="30"
								variant="text"
								color="error"
								v-tooltip="t('pricelist.curves.table.actions.deleteModel')"
								@click="deleteCurve(item.id)"
								data-testid="delete-curve"
							>
								<i class="fa-light fa-trash-can text-error" />
							</v-btn>
						</td>
					</tr>
				</template>
			</v-data-table>
		</v-card>
	</module-loading>
</template>

<style scoped lang="scss">
.curve-item {
	td.name {
		font-weight: bold;

		.cell-inner {
			display: flex;
			gap: 0.25rem;
			align-items: center;
		}

		& .text-name {
			text-overflow: ellipsis;
			overflow: hidden;
			max-width: 356px;
		}
	}

	td.count {
		.cell-inner {
			display: flex;
			align-items: center;
			gap: 0.25rem;
		}

		.count-number {
			min-width: 2ch;
		}
	}

	td.description {
		max-width: 200px;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	td.actions {
		font-weight: bold;
		text-align: right;
	}
}

.chit {
	width: 16px;
	height: 16px;
	border-radius: 50%;
	display: inline-block;
}

.position-relative {
	position: relative;
}

.file-input-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	opacity: 0;
	cursor: pointer;
	z-index: -1; /* Hide it behind the button */
}

.top-row {
	display: grid;
	grid-template-columns: 1fr 1fr auto auto;
	gap: 0.75rem;
}
</style>
